import React, { useEffect } from 'react';
import { useNotification } from '../context/NotificationContext';

export default function ToastContainer() {
  const { toasts, removeToast } = useNotification();

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '400px',
      }}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}

function Toast({ toast, onClose }) {
  const { title, message, type, status, progress } = toast;

  // Auto-dismiss after 30 seconds for completed toasts only
  useEffect(() => {
    if (status === 'completed') {
      const timer = setTimeout(() => {
        onClose();
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
    // Failed toasts stay until manually dismissed
  }, [status, onClose]);

  const typeStyles = {
    info: {
      border: '1px solid #dbeafe',
      progressBar: '#3b82f6',
      badgeBg: '#dbeafe',
      badgeColor: '#1d4ed8',
    },
    success: {
      border: '1px solid #dcfce7',
      progressBar: '#22c55e',
      badgeBg: '#dcfce7',
      badgeColor: '#15803d',
    },
    error: {
      border: '1px solid #fee2e2',
      progressBar: '#ef4444',
      badgeBg: '#fee2e2',
      badgeColor: '#b91c1c',
    },
    warning: {
      border: '1px solid #fef3c7',
      progressBar: '#f59e0b',
      badgeBg: '#fef3c7',
      badgeColor: '#b45309',
    },
  };

  const style = typeStyles[type] || typeStyles.info;

  const statusBadge = {
    pending: { text: '⏸ Pending', bg: '#f3f4f6', color: '#6b7280' },
    running: { text: '⏳ Running', bg: '#dbeafe', color: '#1d4ed8' },
    completed: { text: '✓ Done', bg: '#dcfce7', color: '#15803d' },
    failed: { text: '✗ Failed', bg: '#fee2e2', color: '#b91c1c' },
  };

  const currentStatus = statusBadge[status] || statusBadge.pending;

  return (
    <div
      style={{
        background: '#fff',
        border: style.border,
        borderRadius: '12px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        animation: 'slideIn 0.3s ease-out',
        minWidth: '320px',
      }}
    >
      {/* Progress bar at top */}
      <div
        style={{
          height: '4px',
          background: status === 'failed' ? '#ef4444' : status === 'completed' ? '#22c55e' : style.progressBar,
          transition: 'width 0.3s ease',
        }}
      />
      
      <div style={{ padding: '14px 16px' }}>
        {/* Header: Title + Status badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e3a5f', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </span>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: '999px',
              background: currentStatus.bg,
              color: currentStatus.color,
            }}
          >
            {currentStatus.text}
          </span>
        </div>

        {/* Message */}
        {message && (
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', lineHeight: '1.5' }}>
            {message}
          </div>
        )}

        {/* Progress indicator for running tasks */}
        {status === 'running' && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#6b7280' }}>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2" style={{ borderColor: style.progressBar }}></div>
              <span>Processing... {progress > 0 ? `(${progress}%)` : ''}</span>
            </div>
          </div>
        )}

        {/* Close button for completed/failed toasts */}
        {(status === 'completed' || status === 'failed') && (
          <button
            onClick={onClose}
            style={{
              marginTop: '8px',
              fontSize: '11px',
              color: '#6b7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Dismiss
          </button>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
